



































































































































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import user from '@/store/modules/user';
import CompanyMixin from '@/mixins/CompanyMixin';
import router from '@/router';
import axios from 'axios';
import { ticGetFinancialReportFile, ticSearchCompany } from '@/store/api';
import { financialFileIdsFromUrl } from '@/utils/ticFinancialDocument';

/** Same normalization as backend/pkg/tic normalizeOrganizationNumber. */
function normalizeOrgNr(orgNr: string): string {
  return orgNr.trim().replace(/[-\s]/g, '');
}

function findTicHitForOrgNr(search: any, orgNr: string): any | null {
  const hits = search && search.hits;
  if (!Array.isArray(hits) || hits.length === 0) {
    return null;
  }
  const want = normalizeOrgNr(orgNr);
  if (!want) {
    return null;
  }
  for (const hit of hits) {
    const doc = hit && hit.document;
    const reg = doc && doc.registrationNumber;
    if (typeof reg === 'string' && normalizeOrgNr(reg) === want) {
      return hit;
    }
  }
  return null;
}

function extractTicCompanyIdFromDocument(doc: any): number | undefined {
  const id = doc && doc.companyId;
  if (typeof id === 'number' && !Number.isNaN(id)) {
    return id;
  }
  if (typeof id === 'string') {
    const n = parseInt(id, 10);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function extractTicCompanyId(search: any, orgNr: string): number | undefined {
  const hit = findTicHitForOrgNr(search, orgNr);
  return extractTicCompanyIdFromDocument(hit && hit.document);
}

/** TIC `documentValue2` is the period end, e.g. 2023-12-31. */
function formatAnnualReportPeriod(row: any): string {
  const value = row && typeof row.documentValue2 === 'string' ? row.documentValue2 : '';
  const match = value.match(/^(\d{4})-(\d{2})/);
  if (match) {
    return `${match[1]}-${match[2]}`;
  }
  const sec = row && row.documentDate;
  if (typeof sec !== 'number') {
    return '';
  }
  const date = new Date(sec * 1000);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}`;
}

function pickLatestAnnualReport(documents: any[]): any | null {
  const reports = documents.filter(
    (row) =>
      row &&
      row.companyDocumentType === 'annualReport' &&
      financialFileIdsFromUrl(row.documentUrl) &&
      formatAnnualReportPeriod(row),
  );
  if (reports.length === 0) {
    return null;
  }
  reports.sort((a, b) => {
    const periodA = formatAnnualReportPeriod(a);
    const periodB = formatAnnualReportPeriod(b);
    if (periodA !== periodB) {
      return periodA < periodB ? 1 : -1;
    }
    const dateA = typeof a.documentDate === 'number' ? a.documentDate : 0;
    const dateB = typeof b.documentDate === 'number' ? b.documentDate : 0;
    return dateB - dateA;
  });
  return reports[0];
}

@Component
export default class CardOverview extends CompanyMixin {
  ticCompanyId: number | null = null;
  latestAnnualReport: any | null = null;
  annualReportRequest = 0;

  get annualReportPeriod(): string {
    return this.latestAnnualReport ? formatAnnualReportPeriod(this.latestAnnualReport) : '';
  }

  @Watch('overview', { immediate: true })
  onOverviewReady() {
    void this.loadLatestAnnualReport();
  }

  @Watch('$route.params.OrgNr')
  onOrgNrChanged() {
    void this.loadLatestAnnualReport();
  }

  async loadLatestAnnualReport() {
    const requestId = ++this.annualReportRequest;
    this.latestAnnualReport = null;
    this.ticCompanyId = null;
    if (!this.overview) {
      return;
    }
    try {
      const search = await ticSearchCompany(this.orgNr());
      if (requestId !== this.annualReportRequest) {
        return;
      }
      const orgNr = this.orgNr();
      const hit = findTicHitForOrgNr(search, orgNr);
      const document = hit && hit.document;
      const companyId = extractTicCompanyId(search, orgNr);
      const documents = document && Array.isArray(document.documents) ? document.documents : [];
      const latest = pickLatestAnnualReport(documents);
      if (companyId == null || !latest) {
        return;
      }
      this.ticCompanyId = companyId;
      this.latestAnnualReport = latest;
    } catch (e) {
      console.log(e);
    }
  }

  async openAnnualReport() {
    const report = this.latestAnnualReport;
    const ids = financialFileIdsFromUrl(report && report.documentUrl);
    if (!report || !ids || this.ticCompanyId == null) {
      return;
    }
    const popup = window.open('about:blank', '_blank');
    user.setFetchingData(true);
    user.setFetchingMessage('Genererar PDF...');
    try {
      const buf = await ticGetFinancialReportFile(
        this.ticCompanyId,
        ids.summaryId,
        ids.fileId,
      );
      if (!buf) {
        if (popup) {
          popup.close();
        }
        return;
      }
      const url = window.URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
      if (popup) {
        popup.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch (e) {
      if (popup) {
        popup.close();
      }
      console.log(e);
    } finally {
      user.setFetchingData(false);
    }
  }

  gotoPresentation() {
    if (this.overview) {
      this.openFullscreen();
      this.$router.push({
        name: 'presentation-quick',
        params: { OrgNr: this.overview.OrgNr },
      });
    }
  }

  async gotoMother() {
    if (!this.overview || !this.overview.GROUPMOTHER_ORGNR) {
      return;
    }
    const OrgNr = this.overview.GROUPMOTHER_ORGNR;
    await user.setActiveExternalUser(null);
    await this.$router.push({ name: 'overview', params: { OrgNr } });
  }

  openFullscreen() {
    const elem: any = document.documentElement;
    if (elem.requestFullscreen) {
      return elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      /* Firefox */
      return elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      /* Chrome, Safari and Opera */
      return elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
  /* IE/Edge */
  return elem.msRequestFullscreen();
  }
  }

  async update() {
    if (this.overview) {
      await user.addCompany(this.overview.OrgNr);
    }
  }

  download(template: string, fileName: string) {
    user.setFetchingData(true);
    user.setFetchingMessage('Genererar PDF...');

    axios({
      method: 'get',
      url: this.targetUri(template),
      responseType: 'arraybuffer',
    })
      .then((response) => {
        user.setFetchingData(false);
        this.forceFileDownload(response, fileName);
      })
      .catch(() => {
        user.setFetchingData(false);
        console.log('an error occurred');
      });
  }

  targetUri(template: string) {
    const u = user.user;
    if (!u || !this.overview) {
      return;
    }
    return `/api/pdf/quick/accounting/${u.UserId}/${u.Version}/${
      this.overview.OrgNr
    }/0`;
  }

  forceFileDownload(response: any, fileName: string) {
    const data = new Blob([response.data], { type: 'application/pdf' });

    // IE
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(data, fileName);
      return;
    }

    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      // For Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  get pending() {
    return user.fetchingData && user.fetchingMessage !== 'Genererar PDF...';
  }

  get hasUpdates() {
    if (!this.overview) {
      return false;
    }
    return this.overview.LatestPeriod !== this.overview.CurrentPeriod;
  }

  get hasMother() {
    return false;
    // if (!this.overview) {
    //   return false;
    // }
    // if (this.overview.GROUPMOTHER_ORGNR === this.overview.OrgNr) {
    //   return false;
    // }
    // return user.getInternalCompany(this.overview.GROUPMOTHER_ORGNR) !== null;
  }
}
